// Tutorial data models for the tutorial system

/**
 * TutorialModule - Represents a module in the tutorial structure
 * Supports multi-level content hierarchy
 */
export class TutorialModule {
  constructor(data) {
    this.itemId = data.itemId;
    this.type = data.type; // 'module'
    this.title = data.title;
    this.order = data.order;
    this.parentId = data.parentId || null;
    this.description = data.description || '';
    this.children = data.children || [];
  }

  /**
   * Check if this module has any content items
   */
  hasContent() {
    return this.children.some(child => child.type === 'content');
  }

  /**
   * Get all content items in this module (including nested)
   */
  getAllContent() {
    const content = [];
    
    const collectContent = (items) => {
      items.forEach(item => {
        if (item.type === 'content') {
          content.push(item);
        } else if (item.children && item.children.length > 0) {
          collectContent(item.children);
        }
      });
    };
    
    collectContent(this.children);
    return content;
  }

  /**
   * Get direct child modules
   */
  getChildModules() {
    return this.children.filter(child => child.type === 'module');
  }

  /**
   * Get direct child content
   */
  getChildContent() {
    return this.children.filter(child => child.type === 'content');
  }
}

/**
 * TutorialContent - Represents a content item (video/PDF/text)
 */
export class TutorialContent {
  constructor(data) {
    this.itemId = data.itemId;
    this.type = data.type; // 'content'
    this.title = data.title;
    this.order = data.order;
    this.parentId = data.parentId;
    this.contentType = data.contentType; // 'video', 'pdf', 'text'
    this.s3Key = data.s3Key;
    this.description = data.description || '';
    this.children = []; // Content items don't have children
  }

  /**
   * Check if this content is a video
   */
  isVideo() {
    return this.contentType === 'video';
  }

  /**
   * Check if this content is a PDF
   */
  isPDF() {
    return this.contentType === 'pdf';
  }

  /**
   * Check if this content is text
   */
  isText() {
    return this.contentType === 'text';
  }

  /**
   * Get the file extension based on content type
   */
  getFileExtension() {
    switch (this.contentType) {
      case 'video':
        return 'mp4';
      case 'pdf':
        return 'pdf';
      case 'text':
        return 'txt';
      default:
        return '';
    }
  }
}

/**
 * UserTutorialProgress - Tracks user progress through tutorial content
 */
export class UserTutorialProgress {
  constructor(data) {
    this.userId = data.userId;
    this.tutorialContentStatus = data.tutorial_content_status || {};
    this.hasStatus = data.hasStatus || false;
  }

  /**
   * Check if a specific content item is completed
   */
  isContentCompleted(itemId) {
    const status = this.tutorialContentStatus[itemId];
    return status ? status.completed : false;
  }

  /**
   * Check if a specific content item is required
   */
  isContentRequired(itemId) {
    const status = this.tutorialContentStatus[itemId];
    return status ? status.required : false;
  }

  /**
   * Get completion status for a content item
   */
  getContentStatus(itemId) {
    return this.tutorialContentStatus[itemId] || {
      required: false,
      completed: false
    };
  }

  /**
   * Get all completed content IDs
   */
  getCompletedContentIds() {
    return Object.keys(this.tutorialContentStatus).filter(
      itemId => this.isContentCompleted(itemId)
    );
  }

  /**
   * Get all required content IDs
   */
  getRequiredContentIds() {
    return Object.keys(this.tutorialContentStatus).filter(
      itemId => this.isContentRequired(itemId)
    );
  }

  /**
   * Calculate completion percentage
   */
  getCompletionPercentage() {
    const totalContent = Object.keys(this.tutorialContentStatus).length;
    if (totalContent === 0) return 0;
    
    const completedContent = this.getCompletedContentIds().length;
    return Math.round((completedContent / totalContent) * 100);
  }

  /**
   * Calculate required content completion percentage
   */
  getRequiredCompletionPercentage() {
    const requiredContentIds = this.getRequiredContentIds();
    if (requiredContentIds.length === 0) return 100;
    
    const completedRequiredContent = requiredContentIds.filter(
      itemId => this.isContentCompleted(itemId)
    ).length;
    
    return Math.round((completedRequiredContent / requiredContentIds.length) * 100);
  }

  /**
   * Check if all required content is completed
   */
  isRequiredContentCompleted() {
    return this.getRequiredCompletionPercentage() === 100;
  }
}

/**
 * Helper function to build hierarchical structure from flat data
 */
export const buildTutorialHierarchy = (flatData) => {
  // Create a map to store all items by their itemId
  const itemMap = new Map();
  
  // First pass: create all items and store them in the map
  flatData.forEach(item => {
    let instance;
    if (item.type === 'module') {
      instance = new TutorialModule(item);
    } else if (item.type === 'content') {
      instance = new TutorialContent(item);
    } else {
      return; // Skip unknown types
    }
    
    itemMap.set(item.itemId, instance);
  });
  
  // Second pass: build the hierarchy by adding children to their parents
  const rootItems = [];
  
  itemMap.forEach((item, itemId) => {
    if (!item.parentId || item.parentId === '') {
      // This is a root item
      rootItems.push(item);
    } else {
      // This item has a parent, add it to the parent's children
      const parent = itemMap.get(item.parentId);
      if (parent) {
        parent.children.push(item);
      } else {
        // Parent not found, treat as root item
        rootItems.push(item);
      }
    }
  });
  
  // Sort children by order for each item
  const sortChildren = (items) => {
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        item.children.sort((a, b) => (a.order || 0) - (b.order || 0));
        sortChildren(item.children);
      }
    });
  };
  
  // Sort root items and all children
  rootItems.sort((a, b) => (a.order || 0) - (b.order || 0));
  sortChildren(rootItems);
  
  return rootItems;
}; 